import { useState } from "react";
import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import InitiativeComment from "./InitiativeComment";
import ConfirmDialog from "./ConfirmDialog";

const COMMENT_MAX_LENGTH = 2000;
const VISIBLE_COMMENTS_COUNT = 3;

export default function InitiativeComments({ comments, actions, viewer }) {
  const {
    onAdd: onAddComment,
    onEdit: onEditComment,
    onDelete: onDeleteComment,
  } = actions;
  const { id: currentUserId, isAdmin } = viewer;
  const [commentDraft, setCommentDraft] = useState("");
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState(null);

  const handleSubmitComment = async () => {
    const text = commentDraft.trim();
    if (!text) return;
    setIsSubmittingComment(true);
    const result = await onAddComment(text);
    setIsSubmittingComment(false);
    if (result) setCommentDraft("");
  };

  const styles = {
    sectionHeading: { fontWeight: 700, mb: 1, color: "background.text" },
    commentsList: { gap: 1 },
    emptyComments: { color: "background.textMuted" },
    addCommentRow: {
      display: "flex",
      flexDirection: "column",
      gap: 1,
      mt: 1.5,
    },
    addCommentActions: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    },
    addCommentButton: { textTransform: "none" },
    maxLength: { htmlInput: { maxLength: COMMENT_MAX_LENGTH } },
    showMoreWrap: {
      display: "flex",
      alignItems: "center",
      gap: 1.25,
      mt: 1.5,
    },
    showMoreDivider: {
      flex: 1,
      height: "1px",
      bgcolor: "divider",
    },
    showMoreBtn: {
      cursor: "pointer",
      color: "primary.main",
      userSelect: "none",
      flexShrink: 0,
      "&:hover": { opacity: 0.75 },
    },
  };

  const orderedComments = comments?.length ? [...comments].reverse() : [];
  const visibleComments = showAll
    ? orderedComments
    : orderedComments.slice(0, VISIBLE_COMMENTS_COUNT);
  const hiddenCount = orderedComments.length - VISIBLE_COMMENTS_COUNT;

  return (
    <>
      <Typography variant="subtitle2" sx={styles.sectionHeading}>
        Comments
      </Typography>
      {orderedComments.length > 0 ? (
        <>
          <Stack sx={styles.commentsList}>
            {visibleComments.map((comment, i) => {
              const isAuthor =
                comment.authorId != null && comment.authorId === currentUserId;
              const canEditThis = Boolean(onEditComment) && isAuthor;
              const canDeleteThis =
                Boolean(onDeleteComment) && (isAuthor || isAdmin);

              return (
                <InitiativeComment
                  key={comment.id ?? i}
                  comment={comment}
                  canEdit={canEditThis}
                  canDelete={canDeleteThis}
                  onEditComment={onEditComment}
                  onDeleteComment={setDeleteTargetId}
                />
              );
            })}
          </Stack>
          {hiddenCount > 0 && (
            <Box sx={styles.showMoreWrap}>
              <Box sx={styles.showMoreDivider} />
              <Typography
                variant="caption"
                sx={styles.showMoreBtn}
                onClick={() => setShowAll((prev) => !prev)}
              >
                {showAll ? "Show less" : `Show ${hiddenCount} more`}
              </Typography>
              <Box sx={styles.showMoreDivider} />
            </Box>
          )}
        </>
      ) : (
        <Typography variant="body2" sx={styles.emptyComments}>
          No comments yet.
        </Typography>
      )}
      {onAddComment && (
        <Box sx={styles.addCommentRow}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            placeholder="Add a comment..."
            value={commentDraft}
            onChange={(e) => setCommentDraft(e.target.value)}
            disabled={isSubmittingComment}
            slotProps={styles.maxLength}
          />
          <Box sx={styles.addCommentActions}>
            <Typography variant="caption">
              {commentDraft.length >= COMMENT_MAX_LENGTH
                ? `Character limit reached (${COMMENT_MAX_LENGTH})`
                : ""}
            </Typography>
            <Button
              variant="contained"
              size="small"
              onClick={handleSubmitComment}
              disabled={isSubmittingComment || !commentDraft.trim()}
              sx={styles.addCommentButton}
            >
              Post
            </Button>
          </Box>
        </Box>
      )}

      <ConfirmDialog
        open={deleteTargetId != null}
        onClose={() => setDeleteTargetId(null)}
        onConfirm={() => onDeleteComment(deleteTargetId)}
        title="Delete this comment?"
        message="Deleting this comment is permanent and cannot be undone."
      />
    </>
  );
}
